import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';

/**
 * Sticky CTA that slides in after reading content.
 * Encourages users to convert while reading insights.
 */
export const ArticleCTA: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Show after scrolling down 500px, but verify via scroll listener
    const handleScroll = () => {
      if (window.scrollY > 800) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isDismissed) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50, x: 50 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: 50, x: 50 }}
          transition={{ duration: 0.5, type: 'spring' }}
          className="fixed bottom-8 right-8 z-40 max-w-sm w-full hidden md:block"
        >
          <div className="bg-obsidian border border-sapphire-500/30 p-6 rounded-2xl shadow-2xl relative overflow-hidden group">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-sapphire-500/5 group-hover:bg-sapphire-500/10 transition-colors duration-500" />

            <button
              onClick={() => setIsDismissed(true)}
              className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <div className="relative z-10">
              <h3 className="text-xl font-display font-medium text-white mb-2">Need a system like this?</h3>
              <p className="text-sm text-gray-400 mb-6 leading-relaxed">
                We build high-performance software for ambitious teams. Let's discuss your architecture.
              </p>
              <Link
                to="/contact"
                className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-sapphire-400 hover:text-white transition-colors group/link"
              >
                Work with Us
                <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
